/**
 * G21-CHATGPT global loader and unpublished-theme preview guard.
 * Theme ID: 189854777632. Runs only while the storefront is already in preview mode.
 */
(() => {
  'use strict';

  const EXPECTED_THEME_ID = '189854777632';
  const PREVIEW_OPTIONAL_KEYS = ['_fd', 'pb', 'preview_key'];
  const ROOT_DOMAIN = 'gelinlik21.com.tr';
  const pageUrl = new URL(window.location.href);
  const isPreviewSession =
    pageUrl.searchParams.has('preview_theme_id') || pageUrl.searchParams.has('preview_key');

  const currentScriptUrl = document.currentScript?.src;
  if (currentScriptUrl) {
    const coreUrl = currentScriptUrl.replace(
      /g21-theme-enhancements\.js(?=\?|$)/,
      'g21-theme-enhancements-core-g21chatgpt.js'
    );
    if (coreUrl !== currentScriptUrl && !document.querySelector('script[data-g21-core-loader]')) {
      const coreScript = document.createElement('script');
      coreScript.src = coreUrl;
      coreScript.async = false;
      coreScript.dataset.g21CoreLoader = 'true';
      document.head.append(coreScript);
    }
  }

  if (!isPreviewSession || window.G21PreviewGuard?.installed) return;

  const previewValues = new Map([['preview_theme_id', EXPECTED_THEME_ID]]);
  PREVIEW_OPTIONAL_KEYS.forEach((key) => {
    const value = pageUrl.searchParams.get(key);
    if (value) previewValues.set(key, value);
  });

  const isHttpUrl = (url) => url.protocol === 'http:' || url.protocol === 'https:';
  const isInternalUrl = (url) => {
    const hostname = url.hostname.toLowerCase();
    return (
      url.origin === window.location.origin ||
      hostname === ROOT_DOMAIN ||
      hostname.endsWith(`.${ROOT_DOMAIN}`)
    );
  };

  const protectUrl = (input) => {
    if (input == null || input === '') return input;
    try {
      const sourceIsUrl = input instanceof URL;
      const url = new URL(sourceIsUrl ? input.href : String(input), window.location.href);
      if (!isHttpUrl(url) || !isInternalUrl(url)) return sourceIsUrl ? url : String(input);
      previewValues.forEach((value, key) => url.searchParams.set(key, value));
      return sourceIsUrl ? url : url.href;
    } catch (_error) {
      return input;
    }
  };

  const protectElementUrl = (element, attribute) => {
    if (!(element instanceof Element) || !element.hasAttribute(attribute)) return;
    const original = element.getAttribute(attribute);
    if (!original || original.startsWith('#') || original.startsWith('javascript:')) return;
    const protectedValue = protectUrl(original);
    if (typeof protectedValue === 'string' && protectedValue !== original) {
      element.setAttribute(attribute, protectedValue);
    }
  };

  const buildReturnTo = () => {
    const protectedUrl = protectUrl(new URL(window.location.href));
    return `${protectedUrl.pathname}${protectedUrl.search}${protectedUrl.hash}`;
  };

  const patchLocalizationForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/localization')) return;
    let returnInput = form.querySelector('input[name="return_to"]');
    if (!returnInput) {
      returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      form.append(returnInput);
    }
    returnInput.value = buildReturnTo();
  };

  const patchScope = (scope = document) => {
    if (!(scope instanceof Document || scope instanceof Element || scope instanceof DocumentFragment)) return;
    scope.querySelectorAll?.('a[href]').forEach((element) => protectElementUrl(element, 'href'));
    scope.querySelectorAll?.('[data-product-url]').forEach((element) => protectElementUrl(element, 'data-product-url'));
    scope.querySelectorAll?.('[data-connected-product-url]').forEach((element) =>
      protectElementUrl(element, 'data-connected-product-url')
    );
    scope.querySelectorAll?.('form[action*="/localization"]').forEach(patchLocalizationForm);
  };

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (resource, options) => {
    try {
      if (resource instanceof Request) {
        const protectedRequestUrl = protectUrl(resource.url);
        if (typeof protectedRequestUrl === 'string' && protectedRequestUrl !== resource.url) {
          resource = new Request(protectedRequestUrl, resource);
        }
      } else {
        resource = protectUrl(resource);
      }
    } catch (_error) {
      // Keep Shopify's native request untouched if cloning fails.
    }
    return nativeFetch(resource, options);
  };

  const wrapHistoryMethod = (methodName) => {
    const nativeMethod = history[methodName].bind(history);
    history[methodName] = (state, unused, url) =>
      nativeMethod(state, unused, url == null ? url : protectUrl(url));
  };
  wrapHistoryMethod('replaceState');
  wrapHistoryMethod('pushState');

  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (anchor) protectElementUrl(anchor, 'href');
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const form = event.target instanceof Element
        ? event.target.closest('form[action*="/localization"]')
        : null;
      if (form) patchLocalizationForm(form);
    },
    true
  );

  document.addEventListener('submit', (event) => patchLocalizationForm(event.target), true);
  document.addEventListener('shopify:section:load', (event) => patchScope(event.target));

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element || node instanceof DocumentFragment) patchScope(node);
      });
    });
  });

  const install = () => {
    patchScope(document);
    if (document.documentElement) {
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  };

  window.G21PreviewGuard = Object.freeze({
    installed: true,
    expectedThemeId: EXPECTED_THEME_ID,
    protectUrl,
    patchScope,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  } else {
    install();
  }
})();
